import React from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { matchPath } from "react-router";
import {
  Form,
  FormGroup,
  Label,
  InputGroup,
  InputGroupText,
  InputGroupAddon,
  FormFeedback,
} from "reactstrap";
import { useFormik } from "formik";
import { useMutation } from "react-query";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { signup } from "../api/actions/auth";
import { fetcher } from "../api/client";
import InputWithLoader from "./InputWithLoader";
import LoadingButton from "./LoadingButton";
import Alert from "./Alert";
import DocumentModal from "./DocumentModal";
import TermsAndConditions from "./TermsAndConditions";
import PrivacyPolicy from "./PrivacyPolicy";
import { useThemeState } from "../context/theme";

const defaultValues = {
  email: "",
  password: "",
  confirmPassword: "",
  givenName: "",
  familyName: "",
  couponCode: "",
};

const SignUpSchema = Yup.object().shape({
  givenName: Yup.string().required("Required"),
  familyName: Yup.string().required("Required"),
  couponCode: Yup.string(),
  email: Yup.string().email("InvalidEmail").required("Required"),
  password: Yup.string()
    .min(8, "ShortPassword")
    .matches(/[a-z]/, "MissingLowerCase")
    .matches(/[A-Z]/, "MissingUpperCase")
    .required("Required"),
  confirmPassword: Yup.string()
    .required("Required")
    .matches(/[a-z]/, "MissingLowerCase")
    .matches(/[A-Z]/, "MissingUpperCase"),
});

function SignUpForm({ formik, error, onErrorClose, errorMsg, onGoToLogin }) {
  const { t } = useTranslation("common");
  return (
    <>
      <Alert
        primary="ERROR"
        isOpen={error}
        toggle={onErrorClose}
        secondary={t(`errors.${errorMsg}`)}
        icon="info"
        size="sm"
      />
      <Form onSubmit={formik.handleSubmit} noValidate>
        <FormGroup className="mt-3">
          {/* <Label for="givenName">
            {t("forms.givenName")} <span className="text-accent">*</span>
          </Label> */}
          <InputGroup className="mb-2">
            <InputGroupAddon addonType="prepend">
              <InputGroupText>
                <FontAwesomeIcon icon="user" className="muted mx-auto" />
              </InputGroupText>
            </InputGroupAddon>
            <InputWithLoader
              type="text"
              id="givenName"
              className="border-left-0 rounded-right"
              aria-describedby="emailHelp"
              autoComplete="off"
              placeholder={t("forms.givenName")}
              name="givenName"
              loading={formik.isSubmitting}
              onChange={formik.handleChange}
              value={formik.values.givenName}
              invalid={formik.errors.givenName && formik.touched.givenName}
            />
            <FormFeedback>
              {t(`errors.${formik.errors.givenName}`)}
            </FormFeedback>
          </InputGroup>
        </FormGroup>
        <FormGroup>
          {/* <Label for="familyName">
            {t("forms.familyName")} <span className="text-accent">*</span>
          </Label> */}
          <InputGroup className="mb-2">
            <InputGroupAddon addonType="prepend">
              <InputGroupText>
                <FontAwesomeIcon icon="user" className="muted mx-auto" />
              </InputGroupText>
            </InputGroupAddon>
            <InputWithLoader
              type="text"
              id="familyName"
              aria-describedby="emailHelp"
              autoComplete="off"
              className="border-left-0 rounded-right"
              placeholder={t("forms.familyName")}
              name="familyName"
              loading={formik.isSubmitting}
              onChange={formik.handleChange}
              value={formik.values.familyName}
              invalid={formik.errors.familyName && formik.touched.familyName}
            />
            <FormFeedback>
              {t(`errors.${formik.errors.familyName}`)}
            </FormFeedback>
          </InputGroup>
        </FormGroup>
        <FormGroup>
          {/* <Label for="email">
            {t("forms.email")} <span className="text-accent">*</span>
          </Label> */}
          <InputGroup className="mb-2">
            <InputGroupAddon addonType="prepend">
              <InputGroupText>
                <FontAwesomeIcon icon="envelope" className="muted mx-auto" />
              </InputGroupText>
            </InputGroupAddon>
            <InputWithLoader
              className="border-left-0 rounded-right"
              type="email"
              id="email"
              aria-describedby="emailHelp"
              autoComplete="off"
              placeholder={t("forms.email")}
              name="email"
              loading={formik.isSubmitting}
              onChange={formik.handleChange}
              value={formik.values.email}
              invalid={formik.errors.email && formik.touched.email}
            />
            <FormFeedback>{t(`errors.${formik.errors.email}`)}</FormFeedback>
          </InputGroup>
        </FormGroup>
        <FormGroup>
          {/* <Label for="password">
            {t("forms.password")} <span className="text-accent">*</span>
          </Label> */}
          <InputGroup className="mb-2">
            <InputGroupAddon addonType="prepend">
              <InputGroupText>
                <FontAwesomeIcon icon="lock" className="muted mx-auto" />
              </InputGroupText>
            </InputGroupAddon>
            <InputWithLoader
              className="border-left-0 rounded-right"
              type="password"
              id="password"
              name="password"
              placeholder={t("forms.password")}
              autoComplete="new-password"
              loading={formik.isSubmitting}
              onChange={formik.handleChange}
              value={formik.values.password}
              invalid={formik.errors.password && formik.touched.password}
            />
            <FormFeedback>{t(`errors.${formik.errors.password}`)}</FormFeedback>
          </InputGroup>
        </FormGroup>
        <FormGroup>
          {/* <Label for="">
            {t("forms.confirmPassword")} <span className="text-accent">*</span>
          </Label> */}
          <InputGroup className="mb-2">
            <InputGroupAddon addonType="prepend">
              <InputGroupText>
                <FontAwesomeIcon icon="lock" className="muted mx-auto" />
              </InputGroupText>
            </InputGroupAddon>
            <InputWithLoader
              className="border-left-0 rounded-right"
              type="password"
              id="confirmPassword"
              placeholder={t("forms.confirmPassword")}
              autoComplete="new-password"
              name="confirmPassword"
              loading={formik.isSubmitting}
              onChange={formik.handleChange}
              value={formik.values.confirmPassword}
              invalid={
                formik.errors.confirmPassword && formik.touched.confirmPassword
              }
            />
            <FormFeedback>
              {t(`errors.${formik.errors.confirmPassword}`)}
            </FormFeedback>
          </InputGroup>
        </FormGroup>
        <FormGroup>
          {/* <Label for="">{t("forms.coupon")}</Label> */}
          <InputGroup className="mb-2">
            <InputGroupAddon addonType="prepend">
              <InputGroupText>
                <FontAwesomeIcon icon="tag" className="muted mx-auto" />
              </InputGroupText>
            </InputGroupAddon>
            <InputWithLoader
              className="border-left-0 rounded-right"
              type="text"
              id="couponCode"
              placeholder={t("forms.coupon")}
              name="couponCode"
              loading={formik.isSubmitting}
              onChange={formik.handleChange}
              value={formik.values.couponCode}
              invalid={formik.errors.couponCode && formik.touched.couponCode}
            />
            <FormFeedback>
              {t(`errors.${formik.errors.couponCode}`)}
            </FormFeedback>
          </InputGroup>
        </FormGroup>
        <small>
          <Trans i18nKey="auth.signupAcceptTerms" ns="common">
            Al hacer clic en <strong>'Crear Cuenta'</strong>, aceptas nuestros
            <DocumentModal DocumentComponent={TermsAndConditions}>
              Términos y Condiciones
            </DocumentModal>
            , y nuestro
            <DocumentModal DocumentComponent={PrivacyPolicy}>
              Aviso de Privacidad
            </DocumentModal>
            .
          </Trans>
        </small>
        <div className="mt-4">
          <LoadingButton
            className="animate-up w-100 mb-3"
            color="accent-100"
            type="submit"
            loadingText={t("actions.submitting")}
            loading={formik.isSubmitting}
            id="SIGNUPSTARTER"
          >
            {t("auth.signup")}
          </LoadingButton>
          <hr class="hr-text" data-content="O" />
          {/* <Link to="/signup" className="btn btn-primary animate-up w-100 mb-3">{t('auth.signup')}</Link> */}
          <p>
            {t("auth.alreadyHaveAccount")}{" "}
            {onGoToLogin ? (
              <a href="#" onClick={onGoToLogin}>
                {t("auth.loginTitle")}
              </a>
            ) : (
              <Link to="/login">{t("auth.loginTitle")}</Link>
            )}
          </p>
        </div>
      </Form>
    </>
  );
}

function SignUpFormContainer({
  initialValues = defaultValues,
  payload = {},
  onGoToLogin,
  onSignUpSuccess,
}) {
  const history = useHistory();
  let { pathname } = useLocation();
  let verifyVersion = "1";
  const match = matchPath(pathname, {
    path: "/public-templates/",
    exact: false,
    strict: false,
  });
  const theme = useThemeState();
  const { t, i18n } = useTranslation("common");

  const { mutate: mutateSignup, ...signupState } = useMutation(
    (payload) => fetcher(signup(payload)),
    {
      onSuccess: (data) => {
        if (onSignUpSuccess) {
          onSignUpSuccess(data.data);
        } else {
          history.push("/login", {
            showVerificationMessage: true,
          });
        }
        toast.info(t("successMessages.verifyAccount"));
      },
    }
  );

  if (match?.url === "/public-templates") verifyVersion = "2";

  const formik = useFormik({
    initialValues,
    validationSchema: SignUpSchema,
    onSubmit: async (formValues, actions) => {
      if (formValues.confirmPassword !== formValues.password) {
        actions.setFieldError("password", "PasswordMustMatched");
        actions.setFieldError("confirmPassword", "PasswordMustMatched");
      } else {
        let [language] = i18n.language.split("-");

        if (!["es", "en"].includes(language?.toLowerCase())) {
          language = "en";
        }

        await mutateSignup({
          ...formValues,
          lang: language ?? "en",
          domain: theme.domain,
          isLawyer: theme.domain === "lawyers",
          verifyVersion: verifyVersion,
          payload,
        });
      }

      actions.setSubmitting(false);
    },
  });

  const handleAlertClose = () => {
    signupState.reset();
  };

  return (
    <SignUpForm
      error={signupState.isError}
      onErrorClose={handleAlertClose}
      onGoToLogin={onGoToLogin}
      errorMsg={signupState.error?.message}
      formik={formik}
    />
  );
}

export default SignUpFormContainer;
